/**
 * tRPC Hooks and Provider Setup
 *
 * Configures the tRPC client with HTTP transport and React Query
 */

import { useState } from 'react';
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from '@tanstack/react-query';
import { httpLink, TRPCClientError } from '@trpc/client';
import { trpc } from './client';

/**
 * Create the tRPC client instance
 *
 * Configured with:
 * - Batch link for request batching
 * - API endpoint at /api/trpc
 * - Credentials included for session cookies
 * - Authorization header for JWT tokens
 */
export function createTRPCClient() {
  // In development, use relative URL to go through Vite's proxy
  // In production, use the configured API base URL
  const isDev = import.meta.env.DEV;
  const apiUrl = isDev
    ? '/api/trpc'  // Use Vite proxy in development
    : `${import.meta.env.VITE_API_BASE_URL || ''}/api/trpc`;

  return trpc.createClient({
    links: [
      httpLink({
        url: apiUrl,
        // Include credentials for session cookies AND Authorization header for JWT
        fetch(url, options) {
          // Get access token from localStorage
          const accessToken = localStorage.getItem('accessToken');

          return fetch(url, {
            ...options,
            credentials: 'include',
            headers: {
              ...options?.headers,
              // Add Authorization header if token exists
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
          });
        },
      }),
    ],
  });
}

/**
 * Create Query Client with default options
 * Configured with stale-while-revalidate caching strategy:
 * - staleTime: 5 minutes (data is fresh for 5 minutes)
 * - gcTime: 30 minutes (data is cached for 30 minutes, renamed from cacheTime in v5)
 * - refetchOnWindowFocus: true (refetch when window regains focus)
 * - refetchOnReconnect: true (refetch when reconnecting to network)
 */
/**
 * Handle 401 UNAUTHORIZED errors globally by redirecting to login.
 * Prevents confusing "silent failures" when JWT expires mid-session.
 */
function handleGlobalAuthError(error: unknown) {
  if (
    error instanceof TRPCClientError &&
    error.data?.httpStatus === 401
  ) {
    // Clear stale token
    localStorage.removeItem('accessToken');
    // Redirect to login if not already there
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = `/login?expired=1&returnTo=${encodeURIComponent(window.location.pathname)}`;
    }
  }
}

export function createQueryClient() {
  return new QueryClient({
    // Register the 401 handler on the caches (not defaultOptions) so it ALWAYS fires —
    // even when an individual query/mutation supplies its own onError. defaultOptions
    // callbacks are *overridden* by per-hook callbacks; cache callbacks are not. Without
    // this, protected mutations that define their own onError (e.g. the MacBid pick list's
    // setPicked) would silently revert their optimistic update on an expired token instead
    // of redirecting to login — looking like "the checkbox undoes itself".
    queryCache: new QueryCache({
      onError: handleGlobalAuthError,
    }),
    mutationCache: new MutationCache({
      onError: handleGlobalAuthError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime in TanStack Query v5)
        refetchOnWindowFocus: true, // Refetch when window regains focus
        refetchOnReconnect: true, // Refetch when reconnecting to network
        retry: (failureCount, error) => {
          // Don't retry auth errors
          if (error instanceof TRPCClientError && error.data?.httpStatus === 401) return false;
          return failureCount < 1;
        },
      },
    },
  });
}

/**
 * tRPC Provider Component
 *
 * Wraps your app with tRPC and React Query providers
 *
 * Usage:
 * ```tsx
 * import { TRPCProvider } from '@/lib/trpc/hooks';
 *
 * function App() {
 *   return (
 *     <TRPCProvider>
 *       <YourApp />
 *     </TRPCProvider>
 *   );
 * }
 * ```
 */
export function TRPCProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => createQueryClient());
  const [trpcClient] = useState(() => createTRPCClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
